import { useEffect, useRef, useState } from "react";

const STEPS = [
  {
    n: "1",
    title: "Today — Eduzy",
    body:
      "Make learning simple, affordable, and effective for students and families across India through Eduzy.",
  },
  {
    n: "2",
    title: "Next — More products",
    body:
      "Expand the family of content-and-technology products serving learners, educators, and creators with the same simplicity and care.",
  },
  {
    n: "3",
    title: "Tomorrow — A global ecosystem",
    body:
      "A coherent ecosystem where structured content, automation, and AI compound to make knowledge accessible across boundaries.",
  },
];

export default function Vision() {
  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // 0 when section enters bottom of viewport, 1 when top reaches top of viewport
      const start = vh * 0.85;
      const end = vh * 0.2;
      const total = start - end;
      const raw = (start - rect.top) / total;
      const clamped = Math.max(0, Math.min(1, raw));
      setProgress(clamped);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section
      id="vision"
      data-testid="vision-section"
      className="ct-container scroll-mt-28 py-24 md:py-32"
    >
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="ct-eyebrow">Our Vision</span>
          <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-[#0F172A] sm:text-5xl">
            From India,
            <br />
            <span className="text-[#737373]">to the world.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600">
            Built in India with a global mindset, Contentium Tech is a commitment to
            creating impactful digital experiences that empower learners, creators,
            and communities — wherever they are.
          </p>
        </div>

        <div className="lg:col-span-7">
          <div ref={containerRef} className="relative pl-7">
            {/* dim background line */}
            <span
              aria-hidden="true"
              className="absolute left-0 top-1 h-[calc(100%-0.5rem)] w-px bg-slate-200"
            />
            {/* animated progress line */}
            <span
              aria-hidden="true"
              className="absolute left-0 top-1 w-px origin-top bg-[#0A0A0A] transition-[height] duration-150 ease-out"
              style={{ height: `calc(${progress} * (100% - 0.5rem))` }}
            />
            {/* moving dot */}
            <span
              aria-hidden="true"
              className="absolute -left-[3px] h-2 w-2 -translate-y-1/2 rounded-full bg-[#0A0A0A] shadow-[0_0_0_4px_rgba(10,10,10,0.08)] transition-[top] duration-150 ease-out"
              style={{
                top: `calc(${Math.max(0.04, progress)} * (100% - 0.5rem) + 0.25rem)`,
                opacity: progress > 0.02 ? 1 : 0,
              }}
            />

            <ol className="space-y-10">
              {STEPS.map((s, i) => {
                const stepThreshold = (i + 0.5) / STEPS.length;
                const reached = progress >= stepThreshold - 0.05;
                return (
                  <li key={s.n} className="relative">
                    <span
                      className={`absolute -left-[37px] top-1 grid h-6 w-6 place-items-center rounded-full font-display text-[11px] font-bold transition-colors duration-300 ${
                        reached
                          ? "bg-[#0A0A0A] text-white"
                          : "border border-slate-300 bg-white text-[#0F172A]"
                      }`}
                    >
                      {s.n}
                    </span>
                    <h3 className="font-display text-xl font-semibold text-[#0F172A]">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-base leading-relaxed text-slate-600">
                      {s.body}
                    </p>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
